/* eslint-disable react-hooks/exhaustive-deps */
import { createContext, useContext, useEffect, useState } from "react";
import { NavigateFunction } from "react-router-dom";

import { api } from "../../lib/api";

import { useAuth } from "../auth";
import { usePartnerData } from "../partnerData";

import { IRegisterForm, IUserEditForm } from "../../types/forms";
import { IPatientUser } from "../auth/types";
import {
  IAppointmentInternal,
  IUserData,
  IUserProviderData,
  IUserProviderProps,
} from "./types";

import Toast from "../../utils/toast";
import { storagePrefix } from "../../utils/storage";
import { useAbortableRequest } from "../../hooks/abortableController";

export const UserContext = createContext<IUserProviderData>(
  {} as IUserProviderData
);

export const UserProvider = ({ children }: IUserProviderProps) => {
  const { partnerUrl } = usePartnerData();
  const [userData, setUserData] = useState<IUserData>({} as IUserData);
  const [futureAppointments, setFutureAppointments] = useState<IAppointmentInternal[]>([]);
  const [pastAppointments, setPastAppointments] = useState<IAppointmentInternal[]>([]);
  const { token, patientUser, setPatientData } = useAuth();
  const { createController } = useAbortableRequest();

  const receiveMessage = (event: any) => {
    if (event.data.value === "loginMsg") {
      const response = JSON.parse(event.data.message);

      const { token, userName, userId } = response.data;
      const patientUser: IPatientUser = { userName, userId };
      localStorage.setItem(`${storagePrefix}token`, JSON.stringify(token));
      localStorage.setItem(`${storagePrefix}patientUser`, JSON.stringify(patientUser)
      );

      setPatientData({ token, patientUser });
    }
  };

  const openLoginPopUp = () => {
    window.addEventListener("message", receiveMessage, false);

    window.open(
      "https://login.conectadoc.com.br/",
      "Popup",
      `width=500,height=630, left=${(window.innerWidth - 500) / 2}, top=${(window.innerHeight - 630) / 2
      }`
    );
  };

  const registerUser = async (
    data: IRegisterForm,
    navigate: NavigateFunction,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    setIsLoading(true);

    delete data.acceptTerms;
    delete data.emailConfirmation;
    delete data.passwordConfirmation;
    data.DDDMobilePhone = data.mobilePhone.slice(1, 3);
    data.mobilePhone = data.mobilePhone.replace("-", "").slice(5);

    try {
      await api.post("/Patient/register", data);

      setIsLoading(false);
      await Toast.fire({
        title: "Cadastro realizado com sucesso",
        text: "Você será redirecionado para outra pagina...",
        icon: "success",
      });
      navigate(`/${partnerUrl}`);
      openLoginPopUp();
    } catch (error: any) {
      setIsLoading(false);
      Toast.fire({
        title: "Algo deu errado",
        text:
          error.response.status === 400
            ? (Object.values(error.response.data)[0] as string[])[0]
            : "Por favor, tente novamente mais tarde",
        icon: "warning",
      });
    }
  };

  const editUserData = async (
    data: IUserEditForm,
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
  ) => {
    setIsLoading(true);

    delete data.emailConfirmation;
    delete data.passwordConfirmation;
    data.dddMobilePhone = data.mobilePhone.slice(1, 3);
    data.mobilePhone = data.mobilePhone.replace("-", "").slice(5);

    try {
      await api.put(`/Patient/${patientUser.userId}`, data);
      setIsLoading(false);
      Toast.fire({
        title: "Alterações salvas com sucesso",
        icon: "success",
      });
    } catch (error: any) {
      setIsLoading(false);
      Toast.fire({
        title: "Algo deu errado",
        text:
          error.response.status === 400
            ? (Object.values(error.response.data)[0] as string[])[0]
            : "Por favor, tente novamente mais tarde",
        icon: "warning",
      });
    }
  };

  const getUserData = async () => {
    const abortController = createController(`getUserData${patientUser.userId}`);
    try {
      const response = await api.get(`/Patient/${patientUser.userId}`, {
        signal: abortController.signal
      });
      setUserData(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const getAppointments = async (timeParamValue: number) => {
    const abortController = createController(`getAppointments${timeParamValue}`);
    try {
      const response = await api.get("/Attendance", {
        params: {
          time: timeParamValue,
          paginate: false,
        },
        signal: abortController.signal
      });

      if (timeParamValue === 1) {
        setPastAppointments(response.data);
      } else {
        setFutureAppointments(response.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (token && patientUser?.userId) {
      getUserData();
    }
  }, [token, patientUser]);

  return (
    <UserContext.Provider
      value={{
        userData,
        registerUser,
        editUserData,
        futureAppointments,
        pastAppointments,
        getAppointments,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
